/**
 * @file Profile Store
 *
 * Centralized Zustand store for managing all state and actions related to
 * the user's profile, production data, and account information.
 *
 * Responsibilities:
 * - Holds core profile state (user, account, production, currencies, etc.)
 * - Provides Redux-style actions for fetching, updating, and syncing data
 * - Abstracts common account mutations for safer, cleaner updates
 *
 * This store is heavily tied to authenticated user flows
 * and supports both "Own Profile" and "Other Profile" views.
 */

import axios from 'axios';
import dayjs, { extend } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import omit from 'lodash/omit';
import { v4 as uuidv4 } from 'uuid';
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { createQueryParams, useAuthenticationStore } from '@engelvoelkersamericas/common-ui';

import { CURRENCY_MAP } from '@configs';
import {
  ICodeMapResponse,
  IPriorProductionResponse,
  IPriorProductionTransaction,
  IEliteStatusResponse,
  IEvYears,
  IProductionOverview,
  IDemographics,
  IPersonProfile,
  IUserAccount,
  ProfileAppMode,
  Currency,
  IProfileShop,
  IRoles,
  ITestimonial,
} from '@models';
import { getCurrentYear } from '@utils';
import { dateFormatSave, EVA_DATE_FORMAT } from '@configs/constants';

// Extend dayjs with plugins
extend(customParseFormat);

export interface IYearlyProductionOverview {
  [year: number]: IProductionOverview;
}

export interface IYearlyEliteStatus {
  [year: number]: IEliteStatusResponse;
}

export interface IEvYearOption {
  value: number;
  label: string;
}

export interface IProfileStore {
  // State
  isLoadingUserProfile: boolean;
  isLoadingProductionOverview: boolean;
  isLoadingEvYears: boolean;
  currentEvYear: number;
  evYearOptions: IEvYearOption[];
  profileAppMode: ProfileAppMode;
  contextPersonProfile?: IPersonProfile;
  contextSelectedUserAccount?: IUserAccount;
  codeMap?: ICodeMapResponse;
  eliteStatus?: IEliteStatusResponse;
  eliteStatusByYear: IYearlyEliteStatus;
  evYears?: IEvYears;
  productionOverview?: IProductionOverview & { _fetchedForYear?: number };
  productionOverviewByYear: IYearlyProductionOverview;
  priorProduction?: IPriorProductionResponse;
  priorProductionValues?: IPriorProductionTransaction[];
  selectedProductionOverviewYear: number;
  logoutModalOpen: boolean;
  selectedCurrency?: Currency;
  defaultCurrency?: Currency;
  convertToUsd: boolean;
  roles: IRoles[];

  // Setters (Redux-like actions)
  setIsLoadingUserProfile: (isLoadingUserProfile: boolean) => void;
  setProfileAppMode: (profileAppMode: ProfileAppMode) => void;
  setContextPersonProfile: (personProfile: IPersonProfile) => void;
  setContextSelectedUserAccount: (selectedUserAccount: IUserAccount) => void;
  setLogoutModalOpen: (isOpen: boolean) => void;
  setSelectedProductionOverviewYear: (year: number) => void;
  setCurrenciesAndConversion: (params: { selectedCurrency?: Currency; defaultCurrency?: Currency }) => void;
  setCodeMap: (codeMap: ICodeMapResponse) => void;
  setEliteStatus: (status: IEliteStatusResponse) => void;
  setEliteStatusByYear: (statusByYear: Partial<IYearlyEliteStatus>) => void;
  setProductionOverview: (overview: IProductionOverview & { _fetchedForYear?: number }) => void;
  setProductionOverviewByYear: (overviewByYear: Partial<IYearlyProductionOverview>) => void;
  setPriorProduction: (production: IPriorProductionResponse) => void;
  setPriorProductionValues: (values: IPriorProductionTransaction[]) => void;
  setRoles: (roles: IRoles[]) => void;
  updateRoles: (payload: { userId: string; shops: IProfileShop[] }) => Promise<void>;
  fetchRoles: () => Promise<void>;
  openLogoutModal: () => void;
  closeLogoutModal: () => void;
  fetchCodeMap: () => Promise<void>;
  updateBio: (payload: { id: string; language: string; value: string }) => Promise<void>;
  deleteBio: (payload: { id: string; language: string }) => Promise<void>;
  addTestimonial: (userId: string, payload: ITestimonial) => Promise<void>;
  updateTestimonial: (userId: string, payload: ITestimonial) => Promise<void>;
  deleteTestimonial: (payload: { userId: string; id: string }) => Promise<void>;
  updateTestimonials: (payload: { userId: string; testimonials: ITestimonial[] }) => Promise<void>;
  updateDesignations: (payload: { userId: string; designations: string[]; languages: string[] }) => Promise<void>;
  updateDemographics: (userId: string, demographics: IDemographics) => Promise<void>;
  updateContactInfo: (userId: string, payload: Partial<IPersonProfile & { accounts?: Partial<IUserAccount>[] }>) => Promise<void>;
  fetchEliteStatus: (userId: string, mYear: number) => Promise<void>;
  fetchProductionOverview: (userId: string, year: number) => Promise<void>;
  addPriorProduction: (transactions: IPriorProductionTransaction[]) => Promise<void>;
  editPriorProduction: (transaction: IPriorProductionTransaction) => Promise<void>;
  deletePProduction: (id: string) => Promise<{ success: boolean }>;
  fetchPriorProduction: (userId: string, year?: number) => Promise<void>;
  fetchOtherUserProfile: (userId: string) => Promise<void>;
  fetchOwnUserProfile: (userId: string) => Promise<void>;
  updateProfileAppMode: (profileAppMode: ProfileAppMode) => void;
  updateContextPersonProfile: (personProfile: IPersonProfile) => void;
  updateContextSelectedUserAccount: (selectedUserAccount: IUserAccount) => void;
  updateSelectedProductionOverviewYear: (mYear: number) => void;
  setSelectedCurrency: (currency: Currency) => void;
  setDefaultCurrency: (currency: Currency) => void;
  fetchEvYears: () => Promise<void>;
  getEvYearForDate: (date: string | number) => number | null;
  getCurrentEvYear: () => number;
}

// =========================================
// Internal Helpers
//
// Helper functions for updating and mutating
// the user's selected account data.
// These abstract away common update patterns
// like field changes, array mutations, and
// server synchronization with local store updates.
// =========================================

/**
 * Finds the EV fiscal year that contains the given date.
 * @param evYearsData - The EV years data object mapping year numbers to date ranges
 * @param targetDate - The date to find the EV year for
 * @returns The EV year number if found, null otherwise
 */
const findEvYearForDate = (evYearsData: IEvYears, targetDate: dayjs.Dayjs): number | null => {
  const years = Object.keys(evYearsData);
  for (const year of years) {
    const range = evYearsData[year];
    const start = dayjs(range.start);
    const end = dayjs(range.end);
    if ((targetDate.isAfter(start) || targetDate.isSame(start, 'day')) && (targetDate.isBefore(end) || targetDate.isSame(end, 'day'))) {
      return Number(year);
    }
  }
  return null;
};

/**
 * Updates a user account on the server and synchronizes the local state.
 *
 * This function handles the following operations:
 * 1. Prepares the API payload with the account updates
 * 2. Makes the API call to update the user data
 * 3. Updates the local state in the store if the API call succeeds
 *
 * @param userId - The ID of the user account to update
 * @param payload - A partial user account object containing only the fields to update
 * @param isRoleApiCall - If true, includes firstName and lastName in the payload (for role-related updates)
 * @returns A Promise that resolves when the update is complete
 */
const updateSelectedAccountOnServer = async (
  userId: string,
  payload: Partial<IUserAccount>,
  isRoleApiCall: boolean = false,
): Promise<void> => {
  // Get the current state from the store
  const { contextSelectedUserAccount, updateContextSelectedUserAccount, contextPersonProfile } = useProfileStore.getState();
  // Create a copy of the payload to avoid mutation
  const accountUpdates: Partial<IUserAccount> = { ...payload };

  // Prepare the API payload - we're updating a person profile with account changes
  // The API expects an array of accounts, even though we're only updating one
  // We use a type assertion because we're sending a partial account but the API structure expects complete accounts
  const dataToUpdate: Partial<IPersonProfile> = { accounts: [{ ...accountUpdates, userId } as IUserAccount] };

  // For role-related updates, we need to include the person's name
  // This is required by the API to validate the role change
  if (isRoleApiCall) {
    dataToUpdate.firstName = contextPersonProfile?.firstName;
    dataToUpdate.lastName = contextPersonProfile?.lastName;
  }

  // Make the API call to update the user data
  // The personnel service uses a 204 status code for successful updates (No Content)
  const res = await axios.put(`${PERSONNEL_SERVICE_URL}/users/${userId}`, dataToUpdate);

  // Only update local state if the API call was successful and this isn't a role API call
  // Role API calls are handled differently in the UI
  if (res.status === 204 && !isRoleApiCall) {
    // Create a new user account object by merging the current account with the updates
    const tmpUserAccount: IUserAccount = { ...contextSelectedUserAccount, ...accountUpdates };

    // Update the context selected user account in the profile store
    // This ensures the UI reflects the changes immediately
    updateContextSelectedUserAccount(tmpUserAccount);
  }
};

/**
 * Updates a single top-level field on the selected user account in Zustand,
 * syncing the change to the server first.
 *
 * @param userId - The user account ID
 * @param field - The field key of IUserAccount to update
 * @param value - The value to assign to the field
 */
const updateAccountField = async <K extends keyof IUserAccount>(userId: string, field: K, value: IUserAccount[K]): Promise<void> => {
  await updateSelectedAccountOnServer(userId, { [field]: value });

  const selectedAccount = useProfileStore.getState().contextSelectedUserAccount;
  const updatedAccount = {
    ...selectedAccount,
    [field]: value,
  };

  useProfileStore.getState().setContextSelectedUserAccount(updatedAccount);
};

/**
 * Safely mutates and persists an array field on the currently selected user account.
 * This wraps common patterns for modifying testimonial or bio arrays (add, update, delete),
 * ensuring immutability, server sync, and local Zustand state update.
 *
 * Pass in a `mutateFn` that accepts the current array and returns the updated array.
 *
 * @param userId - The user account ID being updated
 * @param field - The array field key on IUserAccount (e.g., 'bio', 'testimonials')
 * @param mutateFn - A function that returns the modified array
 */
const modifyAndSaveArrayField = async <K extends keyof IUserAccount, T = IUserAccount[K] extends Array<infer U> ? U : never>(
  userId: string,
  field: K,
  mutateFn: (arr: T[]) => T[],
): Promise<void> => {
  const state = useProfileStore.getState();
  const array = [...((state.contextSelectedUserAccount?.[field] || []) as unknown as T[])];
  const updatedArray = mutateFn(array);
  await updateAccountField(userId, field, updatedArray as unknown as IUserAccount[K]);
};

// Create store with proper devtools configuration
// Using the recommended pattern for Zustand 4.x with middleware
export const useProfileStore = create<IProfileStore>()(
  subscribeWithSelector(
    devtools(
      (set, get) => ({
        // Initial state

        isLoadingUserProfile: false,
        isLoadingProductionOverview: false,
        isLoadingEvYears: false,
        currentEvYear: getCurrentYear(),
        evYearOptions: [{ value: getCurrentYear(), label: 'YTD' }],
        profileAppMode: ProfileAppMode.OWN_PROFILE,
        contextPersonProfile: undefined,
        contextSelectedUserAccount: {} as IUserAccount,
        codeMap: undefined,
        eliteStatus: undefined,
        eliteStatusByYear: {},
        evYears: undefined,
        productionOverview: undefined,
        productionOverviewByYear: {},
        priorProduction: undefined,
        priorProductionValues: undefined,
        selectedProductionOverviewYear: getCurrentYear(),
        logoutModalOpen: false,
        selectedCurrency: undefined,
        defaultCurrency: undefined,
        roles: undefined,
        convertToUsd: false,

        // Setters (Redux-like actions)
        setIsLoadingUserProfile: (isLoadingUserProfile: boolean) => {
          set({ isLoadingUserProfile }, false, 'setIsLoadingUserProfile');
        },

        setProfileAppMode: (profileAppMode: ProfileAppMode) => {
          set({ profileAppMode }, false, 'setProfileAppMode');
        },

        setContextPersonProfile: (personProfile: IPersonProfile) => {
          set({ contextPersonProfile: personProfile }, false, 'setContextPersonProfile');
        },

        setContextSelectedUserAccount: (selectedUserAccount: IUserAccount) => {
          set({ contextSelectedUserAccount: selectedUserAccount }, false, 'setContextSelectedUserAccount');
        },

        setLogoutModalOpen: (isOpen: boolean) => {
          set({ logoutModalOpen: isOpen }, false, 'setLogoutModalOpen');
        },

        setSelectedProductionOverviewYear: (year: number) => {
          set({ selectedProductionOverviewYear: year }, false, 'setSelectedProductionOverviewYear');
          // fetch production overview for the selected year
          void get().fetchProductionOverview(get().contextSelectedUserAccount?.userId, year);
        },

        setCodeMap: (codeMap: ICodeMapResponse) => {
          set({ codeMap }, false, 'setCodeMap');
        },

        setEliteStatus: (status: IEliteStatusResponse) => {
          set({ eliteStatus: status }, false, 'setEliteStatus');
        },

        setEliteStatusByYear: (statusByYear: Partial<IYearlyEliteStatus>) => {
          const state = get();
          set(
            {
              eliteStatusByYear: { ...state.eliteStatusByYear, ...statusByYear },
            },
            false,
            'setEliteStatusByYear',
          );
        },

        setProductionOverview: (overview: IProductionOverview & { _fetchedForYear?: number }) => {
          set({ productionOverview: overview }, false, 'setProductionOverview');
        },

        setProductionOverviewByYear: (overviewByYear: Partial<IYearlyProductionOverview>) => {
          const state = get();
          set(
            {
              productionOverviewByYear: { ...state.productionOverviewByYear, ...overviewByYear },
            },
            false,
            'setProductionOverviewByYear',
          );
        },

        setPriorProduction: (production: IPriorProductionResponse) => {
          set({ priorProduction: production }, false, 'setPriorProduction');
        },

        setPriorProductionValues: (values: IPriorProductionTransaction[]) => {
          set({ priorProductionValues: values }, false, 'setPriorProductionValues');
        },

        setRoles: (roles: IRoles[]) => {
          set({ roles }, false, 'setRoles');
        },

        setCurrenciesAndConversion: (params: { selectedCurrency?: Currency; defaultCurrency?: Currency }) => {
          const { selectedCurrency, defaultCurrency } = params;
          const state = get();
          const newState: Partial<IProfileStore> = {};

          if (selectedCurrency !== undefined) {
            newState.selectedCurrency = selectedCurrency;
          }

          if (defaultCurrency !== undefined) {
            newState.defaultCurrency = defaultCurrency;
          }

          // Update convertToUsd based on the new currencies
          const effectiveSelected = selectedCurrency ?? state.selectedCurrency;
          const effectiveDefault = defaultCurrency ?? state.defaultCurrency;

          if (effectiveSelected && effectiveDefault) {
            newState.convertToUsd = effectiveSelected !== effectiveDefault;
          }

          set(newState, false, 'setCurrenciesAndConversion');
        },
        fetchRoles: async () => {
          const res = await axios.get(`${PERSONNEL_SERVICE_URL}/roles`);
          // Add type assertion to ensure res.data is treated as an array
          get().setRoles(res.data as IRoles[]);
        },
        // Simple actions that just call setters
        openLogoutModal: () => get().setLogoutModalOpen(true),
        closeLogoutModal: () => get().setLogoutModalOpen(false),
        // Update methods handle complex state changes, side effects, or multiple related changes
        updateProfileAppMode: (profileAppMode: ProfileAppMode) => {
          // For now, this just updates the state, but could be extended with side effects if needed
          get().setProfileAppMode(profileAppMode);
        },
        updateContextPersonProfile: (personProfile: IPersonProfile) => {
          // For now, this just updates the state, but could be extended with side effects if needed
          get().setContextPersonProfile(personProfile);
        },
        updateContextSelectedUserAccount: (selectedUserAccount: IUserAccount) => {
          // First set isLoading to true at the beginning of the operation
          set({ isLoadingUserProfile: true }, false, 'updateContextSelectedUserAccount/loading');

          // Get current state values we need
          const { contextPersonProfile, selectedCurrency, defaultCurrency } = get();

          // Create a state update object
          const stateUpdate: Partial<IProfileStore> = {
            contextSelectedUserAccount: selectedUserAccount,
            contextPersonProfile,
            isLoadingUserProfile: false, // Will be set to true initially, then false at the end
          };

          // Initialize currency based on user's country when user account changes
          if (selectedUserAccount?.userId) {
            // Determine the appropriate currency code based on country
            let currencyToUse: Currency = 'USD'; // Default to USD

            const country = selectedUserAccount?.primaryShopCountry;
            if (country && country in CURRENCY_MAP) {
              currencyToUse = CURRENCY_MAP[country];
            }

            // Add currency updates to the state update object if needed
            const needsSelectedCurrency = !selectedCurrency;
            const needsDefaultCurrency = !defaultCurrency;

            // Only update the currencies if they're not already set
            if (needsSelectedCurrency) {
              stateUpdate.selectedCurrency = currencyToUse;
            }

            if (needsDefaultCurrency) {
              stateUpdate.defaultCurrency = currencyToUse;
            }

            // If we're setting either currency, we need to update the convertToUsd flag
            if (needsSelectedCurrency || needsDefaultCurrency) {
              // Figure out what the effective currencies will be after this update
              const effectiveSelectedCurrency = stateUpdate.selectedCurrency || selectedCurrency;
              const effectiveDefaultCurrency = stateUpdate.defaultCurrency || defaultCurrency;

              // Only set convertToUsd if both currencies are defined
              if (effectiveSelectedCurrency && effectiveDefaultCurrency) {
                // convertToUsd is true when the currencies don't match
                stateUpdate.convertToUsd = effectiveSelectedCurrency !== effectiveDefaultCurrency;
              }
            }
          }

          // Update the account in the person profile if it exists
          if (contextPersonProfile?.accounts) {
            contextPersonProfile.accounts = contextPersonProfile.accounts.map((mObj) => {
              if (mObj.userId === selectedUserAccount.userId) {
                return selectedUserAccount;
              }
              return mObj;
            });
          }

          // Update all state in one operation
          set({ ...stateUpdate }, false, 'updateContextSelectedUserAccount');
        },

        fetchCodeMap: async (): Promise<void> => {
          try {
            // Check if we already have code map data
            const state = get();
            if (state.codeMap) {
              // Use direct set call with fromCache action name for better debugging
              set({ codeMap: state.codeMap }, false, 'fetchCodeMap/fromCache');
              return;
            }

            // Set loading state for operations that affect global data
            set({ isLoadingUserProfile: true }, false, 'fetchCodeMap/loading');

            const queryParams = {
              types: [
                'languages',
                'specializations',
                'designations',
                'genders',
                'ethnicities',
                'veteran-status',
                'degrees',
                'sexual-identities',
                'pronouns',
                'phone-types',
                'social-media-platforms',
                'biography-languages',
                'testimonial-languages',
                'pp-types',
              ],
            };

            // Fetch code map from server
            const codeMapResp = await axios.get(`${PERSONNEL_SERVICE_URL}/maps${createQueryParams(queryParams)}`);
            const codeMapResult = codeMapResp.data as ICodeMapResponse;

            // Process the code map data to add label and value properties
            Object.keys(codeMapResult).forEach((key) =>
              codeMapResult[key].forEach((item) => {
                item.label = item.decode;
                item.value = item.code;
              }),
            );

            // Update the code map in the store
            set({ codeMap: codeMapResult, isLoadingUserProfile: false }, false, 'fetchCodeMap/success');
          } catch (error) {
            // Reset loading state on error
            set({ isLoadingUserProfile: false }, false, 'fetchCodeMap/error');
            console.error('Error fetching code map:', error);
            throw error; // Re-throw to allow components to handle the error
          }
        },
        updateDesignations: async (payload: { userId: string; designations: string[]; languages: string[] }) => {
          const { designations, languages, userId } = payload;
          await updateSelectedAccountOnServer(userId, { designations, languages });
        },
        updateRoles: async (payload: { userId: string; shops: IProfileShop[] }) => {
          try {
            const { userId, shops } = payload;

            // Update on server with force refresh flag
            await updateSelectedAccountOnServer(userId, { shops: shops }, true);

            // Get store access
            const { fetchUserProfile: fetchAuthUserProfile } = useAuthenticationStore.getState();
            const { profileAppMode } = get();

            // Update profiles based on mode
            if (profileAppMode === ProfileAppMode.OTHER_PROFILE) {
              // For other profile, use our own method
              await get().fetchOtherUserProfile(userId);
            } else {
              // For own profile, refresh auth user profile and fetch user profile
              await fetchAuthUserProfile();
              await get().fetchOwnUserProfile(userId);
            }
          } catch (error) {
            console.error('Error updating roles:', error);
            throw error; // Re-throw to allow components to handle the error
          }
        },
        updateBio: async ({ id, language, value }) => {
          await modifyAndSaveArrayField<'bio'>(id, 'bio', (bio) => {
            const existing = bio.find((b) => b.language === language);
            if (existing) {
              return bio.map((b) => (b.language === language ? { language, value } : b));
            }
            return [...bio, { language, value }];
          });
        },

        deleteBio: async ({ id, language }) => {
          await modifyAndSaveArrayField<'bio'>(id, 'bio', (bio) => bio.filter((b) => b.language !== language));
        },

        addTestimonial: async (userId, payload) => {
          const id = payload.id || uuidv4();
          const newTestimonial = { ...payload, id, rate: 5 };

          await modifyAndSaveArrayField<'testimonials'>(userId, 'testimonials', (arr) => [
            ...arr,
            { ...newTestimonial, order: arr.length + 1 },
          ]);
        },

        updateTestimonial: async (userId, payload) => {
          await modifyAndSaveArrayField<'testimonials'>(userId, 'testimonials', (arr) =>
            arr.map((t) => (t.id === payload.id ? { ...t, ...payload } : t)),
          );
        },

        updateTestimonials: async ({ userId, testimonials }) => {
          const sorted = [...testimonials].sort((a, b) => a.order - b.order);
          await updateAccountField(userId, 'testimonials', sorted);
        },

        deleteTestimonial: async ({ userId, id }) => {
          await modifyAndSaveArrayField<'testimonials'>(userId, 'testimonials', (arr) => arr.filter((t) => t.id !== id));
        },

        updateDemographics: async (userId: string, demographics: IDemographics) => {
          const res = await axios.put(`${PERSONNEL_SERVICE_URL}/users/${userId}`, { demographics });
          const { contextPersonProfile } = get();
          if (res.status === 204) {
            const tmpUserProfile = { ...contextPersonProfile, demographics };
            set(
              () => ({
                contextPersonProfile: tmpUserProfile,
              }),
              false,
              'updateDemographics',
            );
          }
        },

        updateContactInfo: async (
          userId: string,
          payload: Partial<IPersonProfile & { accounts?: Partial<IUserAccount>[] }>,
        ): Promise<void> => {
          const res = await axios.put(`${PERSONNEL_SERVICE_URL}/users/${userId}`, { ...payload });
          const { contextPersonProfile, contextSelectedUserAccount } = get();

          if (res.status === 204) {
            // Create a typed copy of the payload
            const typedPayload = { ...payload };

            if (typedPayload?.pronouns && typedPayload.pronouns === 'none') {
              // locally we need the value of pronouns to be null if it is 'none'
              typedPayload.pronouns = null;
            }

            // Create properly typed objects for profile and account updates
            const profileUpdates = omit(typedPayload, 'accounts') as Partial<IPersonProfile>;
            const accountUpdates = (typedPayload?.accounts?.[0] as Partial<IUserAccount>) || {};

            const tmpUserProfile = { ...contextPersonProfile, ...profileUpdates };
            const tmpUserAccount = { ...contextSelectedUserAccount, ...accountUpdates };

            set(
              () => ({
                contextPersonProfile: tmpUserProfile,
                contextSelectedUserAccount: tmpUserAccount,
              }),
              false,
              'updateContactInfo',
            );
          }
        },

        fetchEliteStatus: async (userId: string, mYear: number): Promise<void> => {
          try {
            const state = get();
            const hasYearData = state.eliteStatusByYear[mYear];

            if (hasYearData) {
              get().setEliteStatus(hasYearData);
              return;
            }

            const res = await axios.get(`${DEALS_SERVICE_URL}/awards/${userId}?year=${mYear}&year=${mYear - 1}`);

            if (res.status === 200) {
              const eliteStatusData = res.data as IEliteStatusResponse;
              set(
                (state) => ({
                  eliteStatus: eliteStatusData,
                  eliteStatusByYear: {
                    ...state.eliteStatusByYear,
                    [mYear]: eliteStatusData,
                  },
                }),
                false,
                'fetchEliteStatus/success',
              );
            }
          } catch (error) {
            console.error('Error fetching elite status:', error);
            throw error;
          }
        },

        fetchProductionOverview: async (userId: string, year: number): Promise<void> => {
          set({ isLoadingProductionOverview: true });
          try {
            const state = get();

            // Check if we already have data for this specific year in our year-by-year cache
            const hasYearData = state.productionOverviewByYear[year];

            if (hasYearData) {
              // Using cached data for the specific year
              // update the current productionOverview for compatibility
              get().setProductionOverview({
                ...hasYearData,
                _fetchedForYear: year,
              });
              return; // Early return to prevent unnecessary API call
            }
            // Fetch production overview for the requested year
            const res = await axios.get(`${PERSONNEL_SERVICE_URL}/users/${userId}/production-overview/${year}`);

            // Add metadata to track when and for which year this data was fetched
            const enrichedData = {
              ...(res.data as IProductionOverview),
              _fetchedForYear: year,
            };

            // Guard against stale writes: only update productionOverview if this year
            // is still the selected year. This prevents race conditions where an older
            // request (e.g., for 2026) completes after a newer request (e.g., for 2025)
            // and overwrites the correct data.
            const currentSelectedYear = get().selectedProductionOverviewYear;
            const shouldUpdateCurrentView = year === currentSelectedYear;

            // Always cache the data by year, but only update the current view if still relevant
            set(
              (state) => ({
                ...(shouldUpdateCurrentView && { productionOverview: enrichedData }),
                productionOverviewByYear: {
                  ...state.productionOverviewByYear,
                  [year]: res.data as IProductionOverview,
                },
              }),
              false,
              'fetchProductionOverview/success',
            );
          } catch (error) {
            console.error('Error fetching production overview:', error);
            throw error;
          } finally {
            set({ isLoadingProductionOverview: false });
          }
        },

        addPriorProduction: async (transactions: IPriorProductionTransaction[]): Promise<void> => {
          const res = await axios.post(`${DEALS_SERVICE_URL}/v1/prior-production`, [...transactions]);
          if (res.status === 200 || res.status === 201) {
            // Get the current state
            const state = get();
            // Update the state with the new transactions
            const newTransactions = res.data as IPriorProductionTransaction[];
            get().setPriorProductionValues([...(state.priorProductionValues || []), ...newTransactions]);
          }
        },

        editPriorProduction: async (transaction: IPriorProductionTransaction): Promise<void> => {
          const res = await axios.put(`${DEALS_SERVICE_URL}/v1/prior-production/${transaction.id}`, {
            ...transaction,
          });

          if (res.status === 200 || res.status === 204) {
            const state = get();

            // Update the state by replacing the edited transaction
            const updatedTransaction = {
              ...transaction,
              transactionDate: dayjs(transaction.transactionDate).format(dateFormatSave),
            };

            const updatedTransactions =
              state.priorProductionValues?.map((item) =>
                item.id === updatedTransaction.id
                  ? {
                      ...item,
                      ...updatedTransaction,
                    }
                  : item,
              ) || [];

            get().setPriorProductionValues(updatedTransactions);
          }
        },

        fetchPriorProduction: async (userId: string, year?: number): Promise<void> => {
          const state = get();

          if (state.priorProductionValues?.length > 0) {
            if (year) {
              get().setPriorProductionValues(
                state.priorProductionValues.filter((transaction) => {
                  if (!transaction.transactionDate) return false;
                  const dateTime = dayjs(transaction.transactionDate, EVA_DATE_FORMAT);
                  return dateTime.year() === year;
                }),
              );
            }
            return;
          }

          const res = await axios.get(`${DEALS_SERVICE_URL}/v1/prior-production/${userId}`);
          // Add proper type assertion to the API response data
          let filteredData = (res?.data || []) as IPriorProductionTransaction[];

          if (year) {
            filteredData = filteredData.filter((transaction) => {
              if (!transaction.transactionDate) return false;
              const dateTime = dayjs(transaction.transactionDate, EVA_DATE_FORMAT);
              return dateTime.year() === year;
            });
          }

          if (res?.status === 200) {
            get().setPriorProductionValues(filteredData);
          }
        },

        deletePProduction: async (id: string): Promise<{ success: boolean }> => {
          const res = await axios.delete(`${DEALS_SERVICE_URL}/v1/prior-production/${id}`);
          // Assuming the API returns a success indicator or we're creating one
          if (res.status === 204) {
            // update state
            const state = get();
            const updatedTransactions = state.priorProductionValues?.filter((transaction) => transaction.id !== id) || [];
            get().setPriorProductionValues(updatedTransactions);
          }
          return { success: res.status === 204 };
        },

        fetchOtherUserProfile: async (userId: string): Promise<void> => {
          const state = get();

          // Check if we already have data for this user - use cached data only for same user
          const isSameUser = state.contextSelectedUserAccount?.userId?.toLowerCase() === userId.toLowerCase();

          if (isSameUser && state.contextPersonProfile) {
            // Using cached user profile data for the same user
            return;
          }

          try {
            get().setIsLoadingUserProfile(true);
            const res = await axios.get(`${PERSONNEL_SERVICE_URL}/users/${userId}`);

            const profileData = res?.data as IPersonProfile;
            const resultAccount = profileData?.accounts?.find((mObj) => mObj.userId?.toLowerCase() === userId.toLowerCase());

            if (!resultAccount) {
              console.warn(`No matching account found for userId: ${userId}`);
            }

            get().setContextPersonProfile(profileData);
            if (resultAccount) {
              get().setContextSelectedUserAccount(resultAccount);
            }
          } catch (error) {
            console.error('Error fetching other user profile:', error);
            throw error;
          } finally {
            get().setIsLoadingUserProfile(false);
          }
        },

        fetchOwnUserProfile: async (userId: string): Promise<void> => {
          try {
            get().setIsLoadingUserProfile(true);
            const res = await axios.get(`${PERSONNEL_SERVICE_URL}/users/${userId}`);

            const profileData = res?.data as IPersonProfile;
            const resultAccount = profileData?.accounts?.find((mObj) => mObj.userId?.toLowerCase() === userId.toLowerCase());

            if (!resultAccount) {
              console.warn(`No matching account found for userId: ${userId}`);
            }

            get().setContextPersonProfile(profileData);
            if (resultAccount) {
              get().setContextSelectedUserAccount(resultAccount);
            }
          } catch (error) {
            console.error('Error fetching own user profile:', error);
            throw error;
          } finally {
            get().setIsLoadingUserProfile(false);
          }
        },

        updateSelectedProductionOverviewYear: (mYear: number) => {
          get().setSelectedProductionOverviewYear(mYear);
        },

        setSelectedCurrency: (newCurrency: Currency) => {
          const { selectedCurrency } = get();
          if (selectedCurrency !== newCurrency) {
            get().setCurrenciesAndConversion({ selectedCurrency: newCurrency });
          }
        },

        setDefaultCurrency: (newCurrency: Currency) => {
          const { defaultCurrency } = get();
          if (defaultCurrency !== newCurrency) {
            get().setCurrenciesAndConversion({ defaultCurrency: newCurrency });
          }
        },

        fetchEvYears: async (): Promise<void> => {
          const state = get();
          // Return cached data if available
          if (state.evYears) {
            return;
          }

          set({ isLoadingEvYears: true }, false, 'fetchEvYears/loading');
          try {
            const res = await axios.get(`${DEALS_SERVICE_URL}/ev-years`);
            const evYearsData = res.data as IEvYears;

            // Calculate the current EV year based on today's date
            const currentEvYear = findEvYearForDate(evYearsData, dayjs()) ?? dayjs().year();

            // Build year options once (sorted descending, current year labeled as "YTD")
            const evYearOptions = Object.keys(evYearsData)
              .map(Number)
              .filter((year) => year <= currentEvYear)
              .sort((a, b) => b - a)
              .map((year) => ({
                value: year,
                label: year === currentEvYear ? 'YTD' : year.toString(),
              }));

            // Always sync selectedProductionOverviewYear with currentEvYear on initial load.
            // This ensures we use the correct EV fiscal year rather than calendar year.
            set(
              {
                evYears: evYearsData,
                currentEvYear,
                evYearOptions,
                isLoadingEvYears: false,
                selectedProductionOverviewYear: currentEvYear,
              },
              false,
              'fetchEvYears/success',
            );
          } catch (error) {
            console.error('Error fetching EV years:', error);
            set({ isLoadingEvYears: false }, false, 'fetchEvYears/error');
            throw error;
          }
        },

        getEvYearForDate: (date: string | number): number | null => {
          const evYearsData: IEvYears | undefined = get().evYears;
          if (!evYearsData) return null;

          const targetDate = dayjs(date);
          if (!targetDate.isValid()) return null;

          return findEvYearForDate(evYearsData, targetDate);
        },

        getCurrentEvYear: (): number => {
          // Return the stored value (computed once when evYears is fetched)
          return get().currentEvYear;
        },
      }),
      { name: 'ProfileStore' },
    ),
  ),
);
